import React, { useState, useMemo, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { reportsData } from '../data/reports';
import { initialTasks, taskCategories } from '../data/tasks';
import CreateTaskModal from '../components/CreateTaskModal';

const { 
  FiArrowLeft, FiCheckCircle, FiClock, FiMessageSquare, 
  FiTrendingUp, FiLayers, FiSearch, FiMoreVertical, 
  FiPlus, FiCalendar, FiTrash2, FiEdit2, FiStar
} = FiIcons;

const RecommendationsHub = () => {
  const [activeTab, setActiveTab] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  // Initialize tasks from reports + custom tasks
  const [tasks, setTasks] = useState(() => {
    const reportTasks = [];
    Object.values(reportsData).forEach(report => {
      report.sections.forEach(section => {
        section.recommendations.forEach((rec, index) => {
          reportTasks.push({
            id: `report-${report.id}-${section.title}-${index}`,
            text: rec,
            category: section.title,
            source: report.assessmentTitle,
            reportId: report.id,
            status: index % 3 === 0 ? 'completed' : index % 3 === 1 ? 'in-progress' : 'todo',
            priority: index % 2 === 0 ? 'High' : 'Medium',
            createdAt: report.completedDate
          });
        });
      });
    });
    return [...reportTasks, ...initialTasks];
  });

  const categories = ['all', ...new Set(tasks.map(r => r.category))];

  const filteredTasks = tasks.filter(task => {
    const matchesTab = activeTab === 'all' || task.category === activeTab;
    const matchesStatus = statusFilter === 'all' || task.status === statusFilter;
    const matchesSearch = task.text.toLowerCase().includes(searchTerm.toLowerCase()) || 
                         task.source.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesTab && matchesStatus && matchesSearch;
  });

  const stats = {
    total: tasks.length,
    completed: tasks.filter(t => t.status === 'completed').length,
    todo: tasks.filter(t => t.status === 'todo').length
  };

  const toggleTaskStatus = (id) => {
    setTasks(prev => prev.map(task => {
      if (task.id === id) {
        const nextStatus = task.status === 'completed' ? 'todo' : 'completed';
        return { ...task, status: nextStatus };
      }
      return task;
    }));
  };

  const deleteTask = (id) => {
    setTasks(prev => prev.filter(t => t.id !== id));
  };

  const addTask = (newTask) => {
    setTasks(prev => [newTask, ...prev]);
  };

  const getStatusStyle = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-700 border-green-200';
      case 'in-progress': return 'bg-blue-100 text-blue-700 border-blue-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <header className="mb-8">
          <Link to="/dashboard" className="text-gray-600 hover:text-blue-600 flex items-center mb-4 transition-colors">
            <SafeIcon icon={FiArrowLeft} className="mr-2" /> Back to Dashboard
          </Link>
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Action Items Workspace</h1>
              <p className="text-gray-600">Track implementation progress and manage custom strategic tasks.</p>
            </div>
            <div className="flex gap-4">
              <div className="text-center px-4 py-2 bg-white rounded-xl border border-gray-200 shadow-sm">
                <div className="text-2xl font-bold text-blue-600">{Math.round((stats.completed / (stats.total || 1)) * 100)}%</div>
                <div className="text-[10px] uppercase font-bold text-gray-400 tracking-wider">Completion Rate</div>
              </div>
              <button 
                onClick={() => setIsModalOpen(true)}
                className="bg-blue-600 text-white px-6 py-3 rounded-xl font-bold hover:bg-blue-700 shadow-md transition-all flex items-center"
              >
                <SafeIcon icon={FiPlus} className="mr-2" /> Create Task
              </button>
            </div>
          </div>
        </header>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4 mb-8">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1 relative">
              <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input 
                type="text" 
                placeholder="Search tasks, categories, or assessment sources..." 
                className="w-full pl-10 pr-4 py-3 bg-gray-50 border-none rounded-xl focus:ring-2 focus:ring-blue-500"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="flex gap-2 overflow-x-auto pb-2 lg:pb-0 scrollbar-hide">
              {['all', 'todo', 'in-progress', 'completed'].map(status => (
                <button
                  key={status}
                  onClick={() => setStatusFilter(status)}
                  className={`px-4 py-2 rounded-xl text-sm font-bold whitespace-nowrap transition-all border ${
                    statusFilter === status 
                      ? 'bg-slate-900 text-white border-slate-900 shadow-sm' 
                      : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
                  }`}
                >
                  {status.charAt(0).toUpperCase() + status.slice(1).replace('-', ' ')}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-4 gap-8">
          <div className="lg:col-span-1">
            <nav className="space-y-1 sticky top-24">
              {categories.map(cat => (
                <button
                  key={cat}
                  onClick={() => setActiveTab(cat)}
                  className={`w-full flex items-center justify-between px-4 py-3 rounded-xl text-sm font-bold transition-all ${
                    activeTab === cat ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <span className="capitalize">{cat.replace('&', 'and')}</span>
                  <span className={`px-2 py-0.5 rounded-full text-[10px] ${
                    activeTab === cat ? 'bg-blue-200' : 'bg-gray-200 text-gray-500'
                  }`}>
                    {tasks.filter(r => cat === 'all' || r.category === cat).length}
                  </span>
                </button>
              ))}
            </nav>
          </div>

          <div className="lg:col-span-3">
            <div className="space-y-4">
              <AnimatePresence mode="popLayout">
                {filteredTasks.length > 0 ? (
                  filteredTasks.map((task) => (
                    <motion.div 
                      layout
                      key={task.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.95 }}
                      className={`bg-white rounded-2xl border border-gray-200 p-5 hover:shadow-md transition-shadow group ${
                        task.status === 'completed' ? 'opacity-75' : ''
                      }`}
                    >
                      <div className="flex items-start gap-4">
                        <button 
                          onClick={() => toggleTaskStatus(task.id)}
                          className={`mt-1 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
                            task.status === 'completed' 
                              ? 'bg-green-500 border-green-500 text-white' 
                              : 'border-gray-300 hover:border-blue-500 bg-white'
                          }`}
                        >
                          {task.status === 'completed' && <SafeIcon icon={FiCheckCircle} className="w-4 h-4" />}
                        </button>
                        
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded uppercase tracking-wider border ${getStatusStyle(task.status)}`}>
                              {task.status.replace('-', ' ')}
                            </span>
                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded uppercase tracking-wider ${
                              task.priority === 'High' ? 'bg-red-50 text-red-600' : 'bg-orange-50 text-orange-600'
                            }`}>
                              {task.priority} Priority
                            </span>
                            {task.source === 'User Created' && (
                              <span className="text-[10px] font-bold px-2 py-0.5 rounded uppercase tracking-wider bg-purple-50 text-purple-600 border border-purple-100">
                                <SafeIcon icon={FiStar} className="inline mr-1 w-2.5 h-2.5" /> Custom
                              </span>
                            )}
                          </div>
                          <h3 className={`text-gray-900 font-medium mb-3 leading-relaxed text-sm md:text-base ${
                            task.status === 'completed' ? 'line-through text-gray-400' : ''
                          }`}>
                            {task.text}
                          </h3>
                          
                          <div className="flex flex-wrap items-center gap-4 pt-4 border-t border-gray-50 text-xs text-gray-500">
                            <div className="flex items-center gap-1.5">
                              <SafeIcon icon={FiLayers} className="w-3.5 h-3.5" />
                              <span className="font-semibold">{task.category}</span>
                            </div>
                            <div className="flex items-center gap-1.5">
                              <SafeIcon icon={FiTrendingUp} className="w-3.5 h-3.5" />
                              <span>Source: {task.reportId ? (
                                <Link to={`/report/${task.reportId}`} className="text-blue-600 hover:underline">{task.source}</Link>
                              ) : (
                                <span className="text-purple-600 font-medium">{task.source}</span>
                              )}</span>
                            </div>
                            {task.dueDate && (
                              <div className="flex items-center gap-1.5 text-orange-600 font-medium">
                                <SafeIcon icon={FiCalendar} className="w-3.5 h-3.5" />
                                <span>Due: {task.dueDate}</span>
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="flex flex-col gap-2">
                          <button 
                            onClick={() => deleteTask(task.id)}
                            className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          >
                            <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                          </button>
                          <button className="px-3 py-1.5 bg-blue-50 text-blue-700 text-[11px] font-bold rounded-lg hover:bg-blue-100 transition-colors opacity-0 group-hover:opacity-100">
                            Get Help
                          </button>
                        </div>
                      </div>
                    </motion.div>
                  ))
                ) : (
                  <div className="bg-white rounded-2xl border-2 border-dashed border-gray-200 p-20 text-center">
                    <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-4">
                      <SafeIcon icon={FiSearch} className="text-gray-300 text-2xl" />
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-1">No items found</h3>
                    <p className="text-gray-500">Try adjusting your filters or create a new custom action item.</p>
                  </div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>

      <CreateTaskModal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)} 
        onAdd={addTask} 
      />
    </div>
  );
};

export default RecommendationsHub;